import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { TrendingUp, Plus, Trash2, BookOpen, Camera } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const KINDS = ["material", "labor", "expense", "subcontractor"];
const inp = "w-full rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none";
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function JobCosting({ jobId }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canManage = ["owner", "dispatcher", "technician"].includes(user?.role);
  const canDelete = ["owner", "dispatcher"].includes(user?.role);
  const { data } = useQuery({ queryKey: ["costing", jobId], queryFn: async () => (await api.get(`/jobs/${jobId}/costing`)).data });
  const { data: cf } = useQuery({ queryKey: ["custom-fields"], queryFn: async () => (await api.get("/workspace/custom-fields")).data });
  const [f, setF] = useState({ kind: "material", description: "", category: "", qty: 1, unit_cost: 0, from_pricebook: false });
  const [receipt, setReceipt] = useState("");
  const [scanning, setScanning] = useState(false);

  const refresh = () => qc.invalidateQueries({ queryKey: ["costing", jobId] });
  const add = async () => {
    try {
      await api.post(`/jobs/${jobId}/costs`, { ...f, qty: Number(f.qty) || 0, unit_cost: Number(f.unit_cost) || 0, category: f.category || null });
      toast.success("Cost added");
      setF({ kind: "material", description: "", category: "", qty: 1, unit_cost: 0, from_pricebook: false });
      refresh();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/costs/${id}`); refresh(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const scan = async () => {
    if (!receipt.trim()) return;
    setScanning(true);
    try {
      const { data: r } = await api.post(`/jobs/${jobId}/costs/scan-receipt`, { raw_text: receipt });
      toast.success(`Imported ${r.count} line(s)${r.vendor ? ` from ${r.vendor}` : ""}`);
      setReceipt(""); refresh();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setScanning(false);
  };
  const scanPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data: r } = await api.post(`/jobs/${jobId}/costs/scan-receipt-photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (r.count) toast.success(`Imported ${r.count} line(s)${r.vendor ? ` from ${r.vendor}` : ""}`);
      else toast.warning(r.warning || "No line items found on that photo");
      refresh();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setScanning(false); e.target.value = "";
  };

  if (!data) return null;
  const pct = data.margin_pct;
  const tone = pct === null ? "text-slate-400" : pct >= 40 ? "text-emerald-400" : pct >= 20 ? "text-amber-400" : "text-rose-400";
  const tags = cf?.line_item_tags || [];
  const token = localStorage.getItem("sc_token");
  const receiptUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/media/${id}/file?auth=${token}`;

  return (
    <div className="card-tac mt-6 p-5" data-testid="job-costing">
      <div className="mb-4 flex items-center gap-2 font-mono-tac text-xs uppercase tracking-widest text-slate-500">
        <TrendingUp className="h-4 w-4 text-emerald-400" /> Live job costing & margin
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-slate-800 bg-[#0d1420] p-4">
          <div className="font-mono-tac text-[10px] uppercase tracking-widest text-slate-500">Revenue</div>
          <div className="mt-1 font-display text-2xl font-black text-white" data-testid="costing-revenue">{money(data.revenue)}</div>
          <div className="mt-0.5 font-mono-tac text-[10px] text-slate-600">{(data.revenue_source || "").replace(/_/g, " ")}</div>
        </div>
        <div className="rounded-xl border border-slate-800 bg-[#0d1420] p-4">
          <div className="font-mono-tac text-[10px] uppercase tracking-widest text-slate-500">Cost</div>
          <div className="mt-1 font-display text-2xl font-black text-white" data-testid="costing-cost">{money(data.cost_total)}</div>
        </div>
        <div className="rounded-xl border border-slate-800 bg-[#0d1420] p-4">
          <div className="font-mono-tac text-[10px] uppercase tracking-widest text-slate-500">Gross margin</div>
          <div className={`mt-1 font-display text-2xl font-black ${tone}`} data-testid="costing-margin">{money(data.margin)}</div>
        </div>
        <div className="rounded-xl border border-slate-800 bg-[#0d1420] p-4">
          <div className="font-mono-tac text-[10px] uppercase tracking-widest text-slate-500">Margin %</div>
          <div className={`mt-1 font-display text-2xl font-black ${tone}`} data-testid="costing-margin-pct">{pct === null ? " - " : `${pct}%`}</div>
        </div>
      </div>
      <p className="mt-2 font-mono-tac text-[10px] text-slate-600">Review margin before locking the invoice to QuickBooks / Xero.</p>

      {Object.keys(data.by_category || {}).length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(data.by_category).map(([k, v]) => (
            <span key={k} className="rounded-full border border-slate-700 bg-[#0d1420] px-2.5 py-1 text-xs text-slate-300">{k}: <b className="text-white">{money(v)}</b></span>
          ))}
        </div>
      )}

      <div className="mt-4 space-y-2">
        {(data.costs || []).map((c) => (
          <div key={c.id} className="flex items-center gap-3 rounded-lg border border-slate-800 bg-[#0d1420] px-3 py-2 text-sm" data-testid={`cost-row-${c.id}`}>
            {c.receipt_media_id && (
              <a href={receiptUrl(c.receipt_media_id)} target="_blank" rel="noreferrer" data-testid={`cost-receipt-${c.id}`} className="shrink-0">
                <img src={receiptUrl(c.receipt_media_id)} alt="receipt" className="h-9 w-9 rounded object-cover ring-1 ring-slate-700 hover:ring-cyan-500" />
              </a>
            )}
            <span className="rounded bg-slate-800 px-1.5 py-0.5 font-mono-tac text-[9px] uppercase text-slate-400">{c.kind}</span>
            <span className="flex-1 truncate text-white">{c.description}{c.vendor && <span className="ml-1.5 text-[11px] text-slate-500">· {c.vendor}</span>}</span>
            {c.source === "pricebook" && <BookOpen className="h-3.5 w-3.5 text-cyan-400" title="From Pricebook" />}
            {c.source === "receipt_ocr" && <Camera className="h-3.5 w-3.5 text-amber-400" title="From receipt" />}
            <span className="font-mono-tac text-xs text-slate-500">{c.qty} × {money(c.unit_cost)}</span>
            <span className="w-20 text-right font-mono-tac text-white">{money(c.amount)}</span>
            {canDelete && <button data-testid={`cost-delete-${c.id}`} onClick={() => del(c.id)} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>}
          </div>
        ))}
        {!(data.costs || []).length && <p className="text-sm text-slate-600">No costs logged yet.</p>}
      </div>

      {canManage && (
        <div className="mt-4 grid gap-2 rounded-xl border border-slate-800 p-3 sm:grid-cols-[110px_1fr_130px_80px_110px_auto]">
          <select data-testid="cost-kind" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className={inp}>
            {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <input data-testid="cost-desc" list="cost-tag-list" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Item / description" className={inp} />
          <input data-testid="cost-category" list="cost-cat-list" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="Tag (optional)" className={inp} />
          <input data-testid="cost-qty" type="number" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} placeholder="Qty" className={inp} />
          <input data-testid="cost-unit" type="number" value={f.unit_cost} onChange={(e) => setF({ ...f, unit_cost: e.target.value })} placeholder="Unit $" disabled={f.from_pricebook} className={`${inp} disabled:opacity-50`} />
          <button data-testid="cost-add-btn" onClick={add} disabled={!f.description} className="btn-amber inline-flex items-center justify-center gap-1 rounded-lg px-3 text-xs disabled:opacity-50"><Plus className="h-3.5 w-3.5" /> Add</button>
          <datalist id="cost-cat-list">{tags.map((t) => <option key={t} value={t} />)}</datalist>
          <datalist id="cost-tag-list" />
          <label className="col-span-full flex items-center gap-2 text-xs text-slate-400">
            <input data-testid="cost-from-pricebook" type="checkbox" checked={f.from_pricebook} onChange={(e) => setF({ ...f, from_pricebook: e.target.checked })} className="accent-cyan-500" />
            Pull unit cost from Company Pricebook (match by description)
          </label>
        </div>
      )}

      {canManage && (
        <div className="mt-3 rounded-xl border border-dashed border-slate-700 p-3" data-testid="receipt-import">
          <div className="mb-2 flex items-center gap-2 font-mono-tac text-[10px] uppercase tracking-widest text-slate-500"><BookOpen className="h-3.5 w-3.5 text-cyan-400" /> Auto field costs - import from receipt</div>
          <textarea data-testid="receipt-text" value={receipt} onChange={(e) => setReceipt(e.target.value)} rows={3}
            placeholder="Paste receipt text (vendor + line items + total). We'll auto-create vendor-tagged material costs." className={`${inp} resize-none`} />
          <button data-testid="receipt-scan-btn" onClick={scan} disabled={scanning || !receipt.trim()} className="btn-amber mt-2 inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs disabled:opacity-50">{scanning ? "Reading…" : "Import receipt"}</button>
          <label data-testid="receipt-photo-label" className="ml-2 mt-2 inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-slate-700 px-4 py-1.5 text-xs text-slate-300 hover:border-cyan-500">
            <Camera className="h-3.5 w-3.5" /> Snap / upload photo
            <input data-testid="receipt-photo-input" type="file" accept="image/*" capture="environment" onChange={scanPhoto} className="hidden" />
          </label>
        </div>
      )}
    </div>
  );
}
